import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Globe, DollarSign, Shield, MapPin, Plus, Trash2, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AdminShippingSettings = () => {
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(false);
  const [carriers, setCarriers] = useState([
    { id: '1', name: 'FedEx', status: 'active', apiSupport: true },
    { id: '2', name: 'Delhivery', status: 'active', apiSupport: true },
    { id: '3', name: 'BlueDart', status: 'active', apiSupport: true },
    { id: '4', name: 'India Post', status: 'active', apiSupport: false }
  ]);
  
  const [zones, setZones] = useState([
    { id: '1', name: 'Local - Same City', tax_rate: '18', base_rate: '50' },
    { id: '2', name: 'Regional - Same State', tax_rate: '18', base_rate: '100' },
    { id: '3', name: 'National - Other States', tax_rate: '18', base_rate: '150' }
  ]);

  const [payoutSettings, setPayoutSettings] = useState({
    deferral_start_event: 'shipped',
    minimum_hold_days: '7',
    auto_release_enabled: true
  });

  const [globalDefaults, setGlobalDefaults] = useState({
    default_preparation_time: '24',
    min_shipping_charge: '40',
    max_cod_amount: '50000'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiCall('/api/shipping-settings/admin');
      if (response) {
        setGlobalDefaults({
          default_preparation_time: response.default_preparation_time?.toString() || '24',
          min_shipping_charge: response.min_shipping_charge?.toString() || '40',
          max_cod_amount: response.max_cod_amount?.toString() || '50000'
        });
        setPayoutSettings({
          deferral_start_event: response.deferral_start_event || 'shipped',
          minimum_hold_days: response.minimum_hold_days?.toString() || '7',
          auto_release_enabled: response.auto_release_enabled !== undefined ? response.auto_release_enabled : true
        });
        if (response.carriers) setCarriers(response.carriers);
        if (response.zones) setZones(response.zones);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const payload = {
        default_preparation_time: parseInt(globalDefaults.default_preparation_time),
        min_shipping_charge: parseFloat(globalDefaults.min_shipping_charge),
        max_cod_amount: parseFloat(globalDefaults.max_cod_amount),
        deferral_start_event: payoutSettings.deferral_start_event,
        minimum_hold_days: parseInt(payoutSettings.minimum_hold_days),
        auto_release_enabled: payoutSettings.auto_release_enabled,
        carriers: carriers,
        zones: zones
      };
      
      await apiCall('/api/shipping-settings/admin', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      toast.success('Shipping settings saved successfully');
    } catch (error) {
      toast.error('Failed to save shipping settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCarrier = () => {
    const newCarrier = {
      id: Date.now().toString(),
      name: 'New Carrier',
      status: 'inactive',
      apiSupport: false
    };
    setCarriers([...carriers, newCarrier]);
  };

  const handleRemoveCarrier = (carrierId) => {
    setCarriers(carriers.filter(c => c.id !== carrierId));
  };

  const handleAddZone = () => {
    const newZone = {
      id: Date.now().toString(),
      name: 'New Zone',
      tax_rate: '18',
      base_rate: '0'
    };
    setZones([...zones, newZone]);
  };

  const handleRemoveZone = (zoneId) => {
    setZones(zones.filter(z => z.id !== zoneId));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Global Shipping Settings</h1>
            <p className="text-gray-600 mt-2">Configure system defaults, carriers, zones, and payout rules</p>
          </div>
          <Button onClick={handleSave} disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>

        {/* Global Defaults */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" />
              <CardTitle>Global Defaults</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                These defaults are applied to new vendors and can be overridden by individual vendors in their settings.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Default Preparation Time (Hours)</Label>
                <Input
                  type="number"
                  value={globalDefaults.default_preparation_time}
                  onChange={(e) => setGlobalDefaults({...globalDefaults, default_preparation_time: e.target.value})}
                  placeholder="24"
                />
              </div>
              <div>
                <Label>Minimum Shipping Charge (₹)</Label>
                <Input
                  type="number"
                  value={globalDefaults.min_shipping_charge}
                  onChange={(e) => setGlobalDefaults({...globalDefaults, min_shipping_charge: e.target.value})}
                  placeholder="40"
                />
              </div>
              <div>
                <Label>Maximum COD Amount (₹)</Label>
                <Input
                  type="number"
                  value={globalDefaults.max_cod_amount}
                  onChange={(e) => setGlobalDefaults({...globalDefaults, max_cod_amount: e.target.value})}
                  placeholder="50000"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payout Deferral Configuration */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-600" />
              <CardTitle>Payout Deferral Configuration</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                <strong>Critical Security Rule:</strong> These settings govern when vendor payouts begin and the minimum hold period. 
                This protects the marketplace from fraudulent returns and disputes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Payout Deferral Starts Upon</Label>
                <Select 
                  value={payoutSettings.deferral_start_event} 
                  onValueChange={(value) => setPayoutSettings({...payoutSettings, deferral_start_event: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shipped">Order Shipped</SelectItem>
                    <SelectItem value="delivered">Order Delivered</SelectItem>
                    <SelectItem value="confirmed">Delivery Confirmed by Customer</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Event that triggers the start of hold period</p>
              </div>
              <div>
                <Label>Minimum Hold Days (Global)</Label>
                <Input
                  type="number"
                  value={payoutSettings.minimum_hold_days}
                  onChange={(e) => setPayoutSettings({...payoutSettings, minimum_hold_days: e.target.value})}
                  placeholder="7"
                />
                <p className="text-xs text-gray-500 mt-1">System-wide minimum before any payout can be released</p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="auto_release"
                checked={payoutSettings.auto_release_enabled}
                onChange={(e) => setPayoutSettings({...payoutSettings, auto_release_enabled: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="auto_release">Enable Automatic Payout Release (after hold period expires)</Label>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Current Configuration:</h4>
              <p className="text-sm text-gray-700">
                Vendor payouts will begin <strong>{payoutSettings.minimum_hold_days} days</strong> after 
                the order is marked as <strong>{payoutSettings.deferral_start_event}</strong>. 
                {payoutSettings.auto_release_enabled ? ' Funds will be automatically released to vendors.' : ' Manual approval required for release.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Carrier Management */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                <CardTitle>Approved Shipping Carriers</CardTitle>
              </div>
              <Button size="sm" onClick={handleAddCarrier}>
                <Plus className="h-4 w-4 mr-2" />
                Add Carrier
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <p className="text-sm text-green-800">
                Manage the master list of shipping carriers available to vendors. Only approved carriers can be used for fulfillment.
              </p>
            </div>

            <div className="space-y-2">
              {carriers.map((carrier) => (
                <div key={carrier.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1 grid grid-cols-3 gap-4">
                    <Input
                      value={carrier.name}
                      onChange={(e) => {
                        const updated = carriers.map(c => 
                          c.id === carrier.id ? {...c, name: e.target.value} : c
                        );
                        setCarriers(updated);
                      }}
                      placeholder="Carrier Name"
                    />
                    <Select 
                      value={carrier.status}
                      onValueChange={(value) => {
                        const updated = carriers.map(c => 
                          c.id === carrier.id ? {...c, status: value} : c
                        );
                        setCarriers(updated);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <Badge variant={carrier.apiSupport ? 'default' : 'secondary'}>
                        {carrier.apiSupport ? 'API Support' : 'Manual Only'}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveCarrier(carrier.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Zone and Tax Mapping */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-purple-600" />
                <CardTitle>Shipping Zones & Tax Mapping</CardTitle>
              </div>
              <Button size="sm" onClick={handleAddZone}>
                <Plus className="h-4 w-4 mr-2" />
                Add Zone
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <p className="text-sm text-purple-800">
                Define geographical shipping zones and link them to tax rates. Used for calculating shipping costs and tax compliance.
              </p>
            </div>

            <div className="space-y-2">
              {zones.map((zone) => (
                <div key={zone.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1 grid grid-cols-3 gap-4">
                    <Input
                      value={zone.name}
                      onChange={(e) => {
                        const updated = zones.map(z => 
                          z.id === zone.id ? {...z, name: e.target.value} : z
                        );
                        setZones(updated);
                      }}
                      placeholder="Zone Name"
                    />
                    <div>
                      <Label className="text-xs">GST Rate (%)</Label>
                      <Input
                        type="number"
                        value={zone.tax_rate}
                        onChange={(e) => {
                          const updated = zones.map(z => 
                            z.id === zone.id ? {...z, tax_rate: e.target.value} : z
                          );
                          setZones(updated);
                        }}
                        placeholder="18"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Base Rate (₹)</Label>
                      <Input
                        type="number"
                        value={zone.base_rate}
                        onChange={(e) => {
                          const updated = zones.map(z => 
                            z.id === zone.id ? {...z, base_rate: e.target.value} : z
                          );
                          setZones(updated);
                        }}
                        placeholder="50"
                      />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveZone(zone.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={loading} size="lg">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save All Settings'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminShippingSettings;
